import * as React from 'react';

import { getLocalTimeZone, today } from '@internationalized/date';
import {
  Button,
  DatePicker,
  type DateValue,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  Money,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { AlertCircle, ChevronDown, ChevronRight, FileText } from 'lucide-react';

import {
  $bookingInvoiceData,
  $bookingInvoiceForm,
  $bookingInvoiceLoading,
  $bookingInvoiceSummary,
  $bookingInvoiceValidation,
  $closeBookingInvoiceDialog,
  $createBookingInvoices,
  $invoicePreviewsByCustomer,
  $isBookingInvoiceDialogOpen,
  $isCreatingInvoices,
  $updateBookingInvoiceForm,
  CreateBookingInvoicesResult,
  CustomerInvoicePreview,
} from '../../_state/bookingInvoice.state';
import { useTranslations } from '../../i18n/useTranslations';

type CustomerSectionProps = {
  preview: CustomerInvoicePreview;
  isExpanded: boolean;
  onToggle: () => void;
};

const CustomerSection: React.FC<CustomerSectionProps> = ({
  preview,
  isExpanded,
  onToggle,
}) => {
  const t = useTranslations();

  return (
    <div className="@CustomerSection rounded-md border border-neutral/20">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-neutral/5"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          )}
          <span className="font-medium">{preview.accountName}</span>
          <span className="text-sm text-neutral/60">
            ({preview.lineItems.length}{' '}
            {preview.lineItems.length === 1
              ? t.bookingDetail.invoiceDialog.lineItem
              : t.bookingDetail.invoiceDialog.lineItems}
            )
          </span>
        </div>
        <span className="font-semibold">
          <Money
            amount={preview.totalAmount}
            currencyCode={preview.currencyCode}
          />
        </span>
      </button>

      {isExpanded && (
        <div className="border-t border-neutral/10 px-4 py-3">
          {preview.loadPreviews.map((loadPreview) => (
            <div key={loadPreview.documentNumber} className="mb-4 last:mb-0">
              <div className="mb-2 flex items-center justify-between text-sm font-medium text-neutral/70">
                <span>
                  {t.bookingDetail.invoiceDialog.loadGroup(
                    loadPreview.documentNumber,
                  )}
                </span>
                <span>
                  <Money
                    amount={loadPreview.subtotal}
                    currencyCode={preview.currencyCode}
                  />
                </span>
              </div>
              <div className="space-y-1 pl-4">
                {loadPreview.lineItems.map((lineItem, idx) => (
                  <div
                    key={`${loadPreview.documentNumber}-${idx}`}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-neutral/60">
                      {lineItem.description}
                    </span>
                    <span className="text-neutral/60">
                      {lineItem.quantity.toFixed(2)} {lineItem.unit} @{' '}
                      <Money
                        amount={lineItem.unitPrice.amount}
                        currencyCode={preview.currencyCode}
                      />{' '}
                      ={' '}
                      <Money
                        amount={lineItem.totalPrice.amount}
                        currencyCode={preview.currencyCode}
                      />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

type BookingInvoiceDialogProps = {
  onSuccess?: (results: CreateBookingInvoicesResult) => void;
};

export const BookingInvoiceDialog: React.FC<BookingInvoiceDialogProps> = ({
  onSuccess,
}) => {
  const t = useTranslations();
  const toast = useToast();

  const isOpen = useAtomValue($isBookingInvoiceDialogOpen);
  const isLoading = useAtomValue($bookingInvoiceLoading);
  const isCreating = useAtomValue($isCreatingInvoices);
  const bookingData = useAtomValue($bookingInvoiceData);
  const form = useAtomValue($bookingInvoiceForm);
  const validation = useAtomValue($bookingInvoiceValidation);
  const summary = useAtomValue($bookingInvoiceSummary);
  const previewsByCustomer = useAtomValue($invoicePreviewsByCustomer);

  const updateForm = useSetAtom($updateBookingInvoiceForm);
  const closeDialog = useSetAtom($closeBookingInvoiceDialog);
  const createInvoices = useSetAtom($createBookingInvoices);

  const [expandedCustomers, setExpandedCustomers] = React.useState<Set<string>>(
    new Set(),
  );

  React.useEffect(() => {
    if (isOpen && previewsByCustomer.size > 0 && expandedCustomers.size === 0) {
      // Auto-expand the first customer
      const firstCustomerId = previewsByCustomer.keys().next().value;
      if (firstCustomerId) {
        setExpandedCustomers(new Set([firstCustomerId]));
      }
    }
  }, [isOpen, previewsByCustomer, expandedCustomers.size]);

  const toggleCustomer = React.useCallback((accountId: string) => {
    setExpandedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  }, []);

  const handleClose = React.useCallback(() => {
    setExpandedCustomers(new Set());
    closeDialog();
  }, [closeDialog]);

  const handleCreate = React.useCallback(async () => {
    try {
      const results = await createInvoices();

      if (results.length === 1) {
        toast.success(
          t.bookingDetail.invoiceDialog.successSingle(
            results[0].documentNumber,
          ),
        );
      } else {
        toast.success(
          t.bookingDetail.invoiceDialog.successMultiple(results.length),
        );
      }

      onSuccess?.(results);
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to create invoices',
      );
    }
  }, [createInvoices, toast, t, onSuccess, handleClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      className="@BookingInvoiceDialog"
    >
      <DialogContent className="max-h-[90vh] w-[700px] max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>
            {t.bookingDetail.invoiceDialog.title(
              bookingData?.bookingNumber ?? '',
            )}
          </DialogTitle>
        </DialogHeader>

        <DialogMain className="flex flex-col gap-6 overflow-y-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-neutral/60">Loading...</span>
            </div>
          ) : (
            <>
              {/* Invoice Date */}
              <div className="flex flex-col gap-2">
                <DatePicker
                  label={t.bookingDetail.invoiceDialog.documentDate}
                  value={form.documentDate as DateValue}
                  onChange={(value) =>
                    updateForm({
                      documentDate: value as typeof form.documentDate,
                    })
                  }
                  defaultValue={today(getLocalTimeZone())}
                />
              </div>

              {/* Summary */}
              <div className="rounded-lg bg-neutral/5 p-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="size-4" />
                  <span>
                    {t.bookingDetail.invoiceDialog.subtitle(
                      summary.customerCount,
                    )}
                  </span>
                </div>
                <div className="mt-2 flex flex-col gap-1">
                  {Array.from(summary.totalsByCurrency.entries()).map(
                    ([currencyCode, amount]) => (
                      <div
                        key={currencyCode}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-neutral/60">
                          {t.bookingDetail.invoiceDialog.totalAmount}
                        </span>
                        <span className="text-lg font-semibold">
                          <Money amount={amount} currencyCode={currencyCode} />
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>

              {/* Validation Errors */}
              {!validation.isValid && (
                <div className="rounded-lg border border-failure/30 bg-failure/10 p-4">
                  <div className="flex items-center gap-2 text-failure">
                    <AlertCircle className="size-4" />
                    <span className="font-medium">
                      {t.bookingDetail.invoiceDialog.validation.title}
                    </span>
                  </div>
                  <ul className="mt-2 list-disc pl-6 text-sm text-failure/80">
                    {validation.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Customer Sections */}
              <div className="flex flex-col gap-3">
                <h3 className="font-medium">
                  {t.bookingDetail.invoiceDialog.preview}
                </h3>
                {Array.from(previewsByCustomer.values()).map((preview) => (
                  <CustomerSection
                    key={preview.accountId}
                    preview={preview}
                    isExpanded={expandedCustomers.has(preview.accountId)}
                    onToggle={() => toggleCustomer(preview.accountId)}
                  />
                ))}
              </div>
            </>
          )}
        </DialogMain>

        <DialogFooter className="flex justify-end gap-2 px-4 py-4">
          <Button variant="text" color="neutral" onPress={handleClose}>
            {t.bookingDetail.invoiceDialog.cancel}
          </Button>
          <Button
            variant="solid"
            color="primary"
            onPress={handleCreate}
            isDisabled={!validation.isValid || isCreating || isLoading}
            icon={<FileText className="size-4" />}
          >
            {isCreating
              ? t.bookingDetail.invoiceDialog.creating
              : t.bookingDetail.invoiceDialog.create(summary.customerCount)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
